import React, { useEffect, useState } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import ActionDropdownItem from "../../components/Common/PageTitle/ActionDropdownItem";
import ActivityLoader from "../../components/Common/ActivityLoader";
import MiniStatCard from "../../components/Banking/MiniStatCard";
import { formatDate, numberFormat } from "../../util/araknerdlib";
import ApprovalModal from "../../components/Transaction/ApprovalModal";
import { useApproval } from "../../DataStore/ApprovalContext";
import CallOut from "../../components/Common/CallOut";
import Pagination from "../../components/Common/Pagination";
import ajaxLedger from "../../util/remote/ajaxLedger";
import { useTheme } from "../../DataStore/ThemeContext";
import Notification from "../../components/Common/Notification";
import LedgerApprovalFilterBar from "../../components/Transaction/LedgerApprovalFilterBar";

const badgeTone = (s) =>
  s === "Approved" ? "success" : s === "Rejected" ? "danger" : "warning";


const LedgerApprovalPage = () => {
  const { approvalList, getTransactions, setCurrentPage, metaData, refresh, getApprovalStats, approvalStats } = useApproval();
  const {setNotify} = useTheme();
  
  const [selected, setSelected] = useState(null); // selected approval request row
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  useEffect(() => {
    getTransactions();
    getApprovalStats();
  }, []);
  
  const PageActions = () => (
    <>
      <ActionDropdownItem iconClass="fa fa-sync" label="Refresh" onClick={refresh} />
    </>
  );

  const handleSelect=(record)=>{
    setSelected(record);
    setShowApprovalModal(true);
  }


  const handleApprove=async(approvalId, comments)=>{
    const server_response = await ajaxLedger.approveEntry({approval_id: approvalId, comments: comments});
    if(server_response.status==="OK"){
      setNotify(<Notification type="success" message={server_response.message || "Ledger entry approved successfully."} closable={false} />);
      setShowApprovalModal(false);
      refresh();
    }else{
      setNotify(<Notification type="danger" message={server_response.message || "Failed to approve ledger entry."} closable={false} />);
    }
   
  }

  const handleReject=async(approvalId, comments)=>{
     const server_response = await ajaxLedger.rejectEntry({approval_id: approvalId, comments: comments});
    if(server_response.status==="OK"){
      setNotify(<Notification type="success" message={server_response.message || "Ledger entry rejected successfully."} closable={false} />);
      setShowApprovalModal(false);
      refresh();
    }else{
      setNotify(<Notification type="danger" message={server_response.message || "Failed to reject ledger entry."} closable={false} />);
    }
   
  }


  const handleFilter = () => {
    getTransactions(true);
  };

  return (
    <>
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Ledger Approval"
          description={"Approve or reject pending ledger transactions before posting to the main ledgers."}
          iconClass="fa fa-check-circle"
          PageActions={PageActions}
           Pagination={
            metaData ? (
              <Pagination
                metaData={metaData}
                onPageChange={(page) => setCurrentPage(page)}
              />
            ) : null
          }
        />
        {/* Summary row */}
        <div className="row">
        <div className="col-md-3">
                    <MiniStatCard
                        icon="fa-hourglass-half"
                        label="Total Pending Approvals"
                        value={numberFormat(approvalStats?.pending_count || 0, 2)}
                        tone="primary"
                        meta=""
                        hint="Number of approvals pending action."
                        loading={false}
                    />
                </div>
 
                <div className="col-md-3">
                    <MiniStatCard
                        icon="fa-money-bill-wave"
                        label="Total Pending Amount"
                        value={numberFormat(approvalStats?.pending_amount || 0, 2)}
                        tone="warning"
                        meta=""
                        hint="Total amount pending approval."
                        loading={false}
                        />
                </div>
        </div>


        

        {/* Main two-column layout */}
        <div className="row mt-4">
          {/* Left: table */}
          <div className="col-lg-12">
            <div className="card custom-card">

                {/* Filters */}
              <LedgerApprovalFilterBar filter={handleFilter} />

              <div className="card-body pt-0">
                
                  <div className="table-responsive">
                    <table className="table table-hover table-striped table-bordered">
                      <thead>
                        <tr>
                          <th>Ref</th>
                          <th>Trans Date</th>
                          <th>Module</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Description</th>
                          <th>Requested By</th>
                          <th>Requested At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(approvalList) && approvalList.map((r) => {
                          const active = selected?.id === r.id;
                          return (
                            <tr
                              key={r.id}
                              onClick={() => handleSelect(r)}
                              style={{ cursor: "pointer" }}
                              className={active ? "selected-row" : ""}
                            >
                             <td>{r.reference_no}</td>
                             <td>{ formatDate(r.transaction_date)}</td>
                              <td>{r.module_name}</td>
                              <td>{numberFormat(r.amount)}</td>
                              <td>
                                <span className={`badge badge-${badgeTone(r.approval_status)}`}>
                                  {r.approval_status}
                                </span>
                              </td>
                              <td>{r.description}</td>
                              <td>{r.requested_by?.username || "—"}</td>
                              <td>{ formatDate(r.created_at)}</td>
                            </tr>
                          );
                        })}

                      </tbody>
                    </table>
                  </div>

                 {approvalList === "404" &&
                  <CallOut
                    type="info"
                    message="No approval requests found. Enable ledger approvals in settings to start using this feature."
                    title="Approvals Unavailable"
                  />}
                {!approvalList && <ActivityLoader width={20} />}

              </div>
            </div>
          </div>

         
        </div>
      </MainContent>
    </PageContainer>
    <ApprovalModal
      show={showApprovalModal}
      onClose={() => setShowApprovalModal(false)}
      handleApprove={handleApprove}
      handleReject={handleReject}
      record={selected}
    />
    </>
  );
};

export default LedgerApprovalPage;
